import { useState, useEffect, createContext, useContext } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Clock, Film, Music, Sparkles } from "lucide-react";
import { ShareButton } from "./ShareToGroup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Context to share homepage data across components
const HomeDataContext = createContext(null);

export function HomeDataProvider({ children }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/events/homepage-data`);
        setData(res.data);
      } catch (error) {
        console.error("Error fetching homepage data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <HomeDataContext.Provider value={{ data, loading }}>
      {children}
    </HomeDataContext.Provider>
  );
}

export function useHomeData() {
  return useContext(HomeDataContext);
}

// Mini card for quick scanning
function QuickCard({ event, index }) {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleDateString("ms-MY", { month: "short" });
    return `${day} ${month}`;
  };

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors group"
    >
      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500/20 to-violet-500/20 flex items-center justify-center shrink-0">
        <span className="text-xs font-bold text-pink-400">{formatDate(event.date)}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate group-hover:text-pink-400 transition-colors">
          {event.title}
        </p>
        <p className="text-xs text-muted-foreground truncate">{event.location}</p>
      </div>
    </div>
  );
}

// Loading skeleton
function QuickListSkeleton() {
  return (
    <div className="glass-card rounded-2xl p-5 border animate-pulse">
      <div className="h-5 w-32 bg-white/10 rounded mb-4" />
      <div className="space-y-2">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-14 bg-white/5 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

// This Weekend Section
export function ThisWeekendQuickOptimized() {
  const { data, loading } = useHomeData();

  if (loading) return <QuickListSkeleton />;
  if (!data?.this_week?.length) return null;

  const events = data.this_week;

  return (
    <div className="glass-card rounded-2xl p-5 border" data-testid="quick-list-weekend">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
          <Clock className="h-4 w-4 text-pink-400" />
          Hujung Minggu Ni
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-emerald-400 font-medium">{events.length} events</span>
          <ShareButton events={events} listTitle="Hujung Minggu Ni" listType="weekend" variant="icon" />
        </div>
      </div>
      <div className="space-y-2">
        {events.map((event, i) => (
          <Link key={event.id} to={`/event/${event.id}`}>
            <QuickCard event={event} index={i} />
          </Link>
        ))}
      </div>
      <div className="flex items-center justify-between mt-4">
        <Link 
          to="/events" 
          className="text-xs text-pink-400 hover:text-pink-300 font-medium"
        >
          Tengok semua →
        </Link>
        <ShareButton events={events} listTitle="Hujung Minggu Ni" listType="weekend" variant="button" />
      </div>
    </div>
  );
}

// Free Events Quick List
export function FreeEventsQuickOptimized() {
  const { data, loading } = useHomeData();

  if (loading) return <QuickListSkeleton />;
  if (!data?.free_events?.length) return null;

  const events = data.free_events;

  return (
    <div className="glass-card rounded-2xl p-5 border border-emerald-500/20" data-testid="quick-list-free">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
          <span className="text-emerald-400">✓</span>
          Percuma Je
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-emerald-400 font-medium bg-emerald-500/10 px-2 py-0.5 rounded-full">FREE</span>
          <ShareButton events={events} listTitle="Event Percuma Malaysia 2026" listType="free" variant="icon" />
        </div>
      </div>
      <div className="space-y-2">
        {events.map((event, i) => (
          <Link key={event.id} to={`/event/${event.id}`}>
            <QuickCard event={event} index={i} />
          </Link>
        ))}
      </div>
      <div className="flex items-center justify-between mt-4">
        <Link 
          to="/free-events" 
          className="text-xs text-emerald-400 hover:text-emerald-300 font-medium"
        >
          100+ lagi event free →
        </Link>
        <ShareButton events={events} listTitle="Event Percuma Malaysia 2026" listType="free" variant="button" />
      </div>
    </div>
  );
}

// Movies Quick
export function MoviesQuickOptimized() {
  const { data, loading } = useHomeData();

  if (loading) return <QuickListSkeleton />;
  if (!data?.movies?.length) return null;

  const movies = data.movies;

  return (
    <div className="glass-card rounded-2xl p-5 border border-rose-500/20" data-testid="quick-list-movies">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
          <Film className="h-4 w-4 text-rose-400" />
          Filem 2026
        </h3>
        <ShareButton events={movies} listTitle="Filem Best 2026 Malaysia" listType="movies" variant="icon" />
      </div>
      <div className="space-y-2">
        {movies.map((movie, i) => (
          <Link key={movie.id} to={`/event/${movie.id}`}>
            <QuickCard event={movie} index={i} />
          </Link>
        ))}
      </div>
      <div className="flex items-center justify-between mt-4">
        <Link 
          to="/events?category=Movies" 
          className="text-xs text-rose-400 hover:text-rose-300 font-medium"
        >
          Semua filem →
        </Link>
        <ShareButton events={movies} listTitle="Filem Best 2026 Malaysia" listType="movies" variant="button" />
      </div>
    </div>
  );
}

// Concerts Quick
export function ConcertsQuickOptimized() {
  const { data, loading } = useHomeData();

  if (loading) return <QuickListSkeleton />;
  if (!data?.concerts?.length) return null;

  const concerts = data.concerts;

  return (
    <div className="glass-card rounded-2xl p-5 border border-violet-500/20" data-testid="quick-list-concerts">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
          <Music className="h-4 w-4 text-violet-400" />
          Konsert 2026
        </h3>
        <ShareButton events={concerts} listTitle="Konsert Malaysia 2026" listType="concerts" variant="icon" />
      </div>
      <div className="space-y-2">
        {concerts.map((concert, i) => (
          <Link key={concert.id} to={`/event/${concert.id}`}>
            <QuickCard event={concert} index={i} />
          </Link>
        ))}
      </div>
      <div className="flex items-center justify-between mt-4">
        <Link 
          to="/events?category=music" 
          className="text-xs text-violet-400 hover:text-violet-300 font-medium"
        >
          Semua konsert →
        </Link>
        <ShareButton events={concerts} listTitle="Konsert Malaysia 2026" listType="concerts" variant="button" />
      </div>
    </div>
  );
}

// Boring Weekend Fix
export function BoringWeekendFixOptimized() {
  const { data, loading } = useHomeData();

  if (loading) return <QuickListSkeleton />;
  if (!data?.random_picks?.length) return null;

  const events = data.random_picks;

  return (
    <div className="glass-card rounded-2xl p-5 border border-amber-500/20" data-testid="quick-list-random">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-400" />
            Bosan? Cuba ni
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Random picks kalau tak tau nak buat apa
          </p>
        </div>
        <ShareButton events={events} listTitle="Idea Hujung Minggu" listType="bored" variant="icon" />
      </div>
      <div className="space-y-2">
        {events.map((event, i) => (
          <Link key={event.id} to={`/event/${event.id}`}>
            <QuickCard event={event} index={i} />
          </Link>
        ))}
      </div>
      <div className="flex justify-end mt-4">
        <ShareButton events={events} listTitle="Idea Hujung Minggu" listType="bored" variant="button" />
      </div>
    </div>
  );
}

// Get total event count from context
export function useEventCount() {
  const { data } = useHomeData();
  return data?.total_events || 144;
}
